<template>
  <DModal :show-close="false" :isBgOverlay="true" :appendToBody="true" :placeInTop="true" :pxFromTop="1" :ariaLabel="title" @close="$emit('close')">
    <div class="d-modal special-day-modal text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <h2>{{ title }}</h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content">
        <Loader v-if="isLoadingExisting"></Loader>
        <template v-else>
          <div class="do-input-group">
            <label class="d-label">{{ $t('vue2.special_days.name') }}</label>
            <input type="text" v-model="form.title" :placeholder="$t('vue2.special_days.name_placeholder')"
                   class="d-input width-100" ref="input_title" v-focus>
          </div>

          <div class="do-input-group">
            <div class="special-day-modal__label-row">
              <label class="d-label">{{ $t('vue2.special_days.classes') }}</label>
              <button type="button" class="special-day-modal__link" @click="toggleAllClasses">
                {{ allClassesSelected ? $t('vue2.special_days.clear') : $t('vue2.special_days.select_all') }}
              </button>
            </div>
            <DSelectCals
              :items="klassCalendars"
              :itemsSelected="selectedClasses"
              :placeholder="$t('vue2.special_days.select_classes')"
              @update="onClassesUpdate">
            </DSelectCals>
            <p class="special-day-modal__hint special-day-modal__hint--warning" v-if="hasTriedSave && selectedClasses.length === 0">
              {{ $t('vue2.special_days.no_classes_selected') }}
            </p>
          </div>

          <div class="do-input-group-flex">
            <div>
              <label class="d-label">{{ $t('vue2.special_days.start_date') }}</label>
              <DateSelector
                :date="startDateMoment"
                :width100="true"
                @changed="onStartChanged">
              </DateSelector>
            </div>
            <div>
              <label class="d-label">{{ $t('vue2.special_days.end_date') }}</label>
              <DateSelector
                :date="endDateMoment"
                :minDate="form.startDate"
                :width100="true"
                @changed="onEndChanged">
              </DateSelector>
            </div>
          </div>

          <div
            class="do-input-group do-input-group--toggler"
            :class="{'do-input-group--toggler--active': form.reduct}"
            @click.stop="form.reduct = !form.reduct">
            <label class="d-label">{{ $t('vue2.special_days.reduct') }}</label>
            <AppToggle :allowChanges="false" :propOn="form.reduct"/>
          </div>
        </template>
      </div>

      <div class="d-modal__buttons">
        <div></div>
        <div>
          <button class="do-bttn" @click="$emit('close')" :disabled="isSaving">
            {{ $t('vue2.special_days.cancel') }}
          </button>
          <button
            class="do-bttn do-bttn--submit"
            :class="{
              'do-bttn--submit--disabled': !isValid || isSaving,
              'do-bttn--submit--loading': isSaving
            }"
            @click="save">
            {{ $t('vue2.special_days.save') }}
          </button>
        </div>
      </div>

      <ModalConfirmation
        v-if="showRemovalWarning"
        :showClose="false"
        :isShowConfirmDeleteMessage="false"
        :appendToBody="true"
        :isSubmitButtonLoading="isSaving"
        @confirm="confirmRemovalAndSave"
        @close="showRemovalWarning = false">
        <template slot="item">
          <h3>Fjern klasser med brikker?</h3>
          <p class="special-day-modal__warning__text">
            Du fjerner klasser, der har brikker for denne omlagte dag: {{ affectedRemovedClassNames.join(', ') }}. Brikkerne slettes ikke automatisk.
          </p>
        </template>
        <template slot="confirmText">Gem alligevel</template>
      </ModalConfirmation>
    </div>
  </DModal>
</template>

<script>
import moment from 'moment'
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import DModal from '../common/DModal'
import Loader from '../common/Loader'
import DSelectCals from '../DSelect/DSelectCals'
import DateSelector from '../datepicker/DateSelector'
import AppToggle from '../common/AppToggle'
import ModalConfirmation from '../Modals/ModalConfirmation'

export default {
  name: 'special-day-modal',
  components: {DModal, Loader, DSelectCals, DateSelector, AppToggle, ModalConfirmation},
  props: {
    schoolId: {
      default: null
    },
    schoolYearId: {
      default: null
    },
    specialDayId: {
      default: null
    },
    klassCalendars: {
      type: Array,
      default: () => []
    },
    // Skoleårets første student-dag — bruges som standard for begge datoer.
    defaultDate: {
      default: null
    }
  },
  directives: {
    focus: {
      inserted (el) {
        setTimeout(() => el.focus(), 150)
      }
    }
  },
  data () {
    const defaultDay = this.defaultDate ? moment(this.defaultDate).format('YYYY-MM-DD') : null
    return {
      isSaving: false,
      isLoadingExisting: false,
      hasTriedSave: false,
      form: {
        title: '',
        startDate: defaultDay,
        endDate: defaultDay,
        reduct: true
      },
      selectedClasses: [],
      originalClassIds: [],
      specialDaySubjectIds: [],
      showRemovalWarning: false,
      removalConfirmed: false
    }
  },
  computed: {
    title () {
      return this.specialDayId
        ? this.$t('vue2.special_days.edit')
        : this.$t('vue2.special_days.create')
    },
    startDateMoment () {
      if (this.form.startDate) {
        return moment(this.form.startDate)
      }
      return this.defaultDate ? moment(this.defaultDate) : moment()
    },
    endDateMoment () {
      if (this.form.endDate) {
        return moment(this.form.endDate)
      }
      return this.startDateMoment
    },
    allClassesSelected () {
      return this.klassCalendars.length > 0 && this.selectedClasses.length === this.klassCalendars.length
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    // Navne på fjernede klasser, der stadig har en brik knyttet til dagen.
    affectedRemovedClassNames () {
      const selectedIds = this.selectedClasses.map(cal => cal.id)
      const removedIds = this.originalClassIds.filter(id => selectedIds.indexOf(id) === -1)
      if (removedIds.length === 0) {
        return []
      }
      return removedIds
        .filter(classId => this.specialDaySubjectIds.some(subjectId => {
          const subject = this.subjectsInHash[subjectId]
          if (!subject) {
            return false
          }
          if (subject.calendar_id === classId) {
            return true
          }
          return Array.isArray(subject.calendar_ids) && subject.calendar_ids.indexOf(classId) > -1
        }))
        .map(classId => this.className(classId))
        .filter(name => !!name)
    },
    isValid () {
      return this.form.title.trim().length > 0 &&
        !!this.form.startDate &&
        !!this.form.endDate &&
        this.form.endDate >= this.form.startDate &&
        this.selectedClasses.length > 0
    }
  },
  mounted () {
    if (this.specialDayId) {
      this.loadExisting()
    }
  },
  methods: {
    onStartChanged (date) {
      this.form.startDate = moment(date).format('YYYY-MM-DD')
      if (!this.form.endDate || this.form.endDate < this.form.startDate) {
        this.form.endDate = this.form.startDate
      }
    },
    onEndChanged (date) {
      this.form.endDate = moment(date).format('YYYY-MM-DD')
    },
    onClassesUpdate (classes) {
      this.selectedClasses = classes.slice()
      // Brugeren har ændret valget — nulstil advarslen, så den kan vises igen.
      this.showRemovalWarning = false
      this.removalConfirmed = false
    },
    className (id) {
      const cal = this.klassCalendars.find(k => k.id === id)
      return cal && cal.name ? cal.name : ''
    },
    toggleAllClasses () {
      this.selectedClasses = this.allClassesSelected ? [] : this.klassCalendars.slice()
    },
    loadExisting () {
      this.isLoadingExisting = true
      docendo.specialDaysShow(this.schoolId, this.schoolYearId, this.specialDayId, data => {
        this.form = {
          title: data.title || '',
          startDate: data.start_date || null,
          endDate: data.end_date || null,
          reduct: data.reduct !== false
        }
        const ids = data.calendar_ids || []
        this.selectedClasses = this.klassCalendars.filter(cal => ids.indexOf(cal.id) > -1)
        this.originalClassIds = ids.slice()
        this.specialDaySubjectIds = Array.isArray(data.subject_ids) ? data.subject_ids.slice() : []
        this.isLoadingExisting = false
      }, () => {
        this.isLoadingExisting = false
        eventbus.toast = {text: this.$t('vue2.special_days.error_toast'), success: false}
      })
    },
    buildAttrs () {
      return {
        title: this.form.title.trim(),
        start_date: this.form.startDate,
        end_date: this.form.endDate,
        reduct: this.form.reduct,
        calendar_ids: this.selectedClasses.map(cal => cal.id)
      }
    },
    save () {
      this.hasTriedSave = true
      if (!this.isValid || this.isSaving) {
        return
      }
      // Advar hvis man fjerner en klasse, der stadig har en brik for dagen.
      if (this.specialDayId && !this.removalConfirmed && this.affectedRemovedClassNames.length > 0) {
        this.showRemovalWarning = true
        return
      }
      this.doSave()
    },
    confirmRemovalAndSave () {
      this.removalConfirmed = true
      this.showRemovalWarning = false
      this.save()
    },
    doSave () {
      this.isSaving = true
      const attrs = this.buildAttrs()
      const onSaved = data => {
        if (data && data.status === 'error') {
          this.onError()
          return
        }
        this.isSaving = false
        eventbus.toast = {text: this.$t('vue2.special_days.saved'), success: true}
        this.$emit('saved')
      }
      if (this.specialDayId) {
        docendo.specialDaysUpdate(this.schoolId, this.schoolYearId, this.specialDayId, attrs, onSaved, this.onError)
      } else {
        docendo.specialDaysCreate(this.schoolId, this.schoolYearId, attrs, data => {
          if (!(data && data.status === 'error')) {
            this.$store.dispatch('trackEvent', 'specialDays_omlagtDag_created')
          }
          onSaved(data)
        }, this.onError)
      }
    },
    onError () {
      this.isSaving = false
      eventbus.toast = {text: this.$t('vue2.special_days.error_toast'), success: false}
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.special-day-modal {
  width: 500px;
  max-width: 92vw;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .12);
  position: relative;
  z-index: 1;
}

.d-modal__content {
  padding: 1rem;
}

.do-input-group {
  margin-bottom: 1rem;
}

.do-input-group .d-input {
  height: 38px;
}

.do-input-group-flex {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.do-input-group-flex > div {
  flex: 1;
  min-width: 0;
}

.do-input-group-flex label.d-label {
  margin-bottom: 0.25rem;
}

/* Ensret datovælger og klassevælger med de øvrige felter (38px, fuld bredde). */
.special-day-modal >>> .datepicker-container,
.special-day-modal >>> .datepicker-input {
  max-width: 100%;
  width: 100%;
}

.special-day-modal >>> .datepicker-input input {
  height: 38px;
  width: 100%;
  border: 1px solid #acb6be;
  border-radius: 4px;
}

.special-day-modal >>> .d-select {
  min-height: 38px;
}

.special-day-modal >>> .d-select__input-look {
  min-height: 38px;
  box-shadow: none;
  border: 1px solid #acb6be;
}

.special-day-modal >>> .d-select__input-look.active {
  border-color: var(--blue);
}

.special-day-modal >>> .d-select__header__search,
.special-day-modal >>> .d-select__header__search.show.input-active {
  box-shadow: none;
}

.do-input-group--toggler {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  padding: 1rem;
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, .1), 0 1px 2px -1px rgba(0, 0, 0, .1);
}

.do-input-group--toggler:hover {
  background: #f0f8ff;
}

.do-input-group--toggler > .d-label {
  margin-bottom: 0;
  cursor: pointer;
}

.do-input-group--toggler.do-input-group--toggler--active > .d-label {
  color: var(--dark-blue);
}

.d-modal__buttons {
  display: flex;
  justify-content: space-between;
  padding: 1rem 2rem;
}

.d-modal__buttons > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.special-day-modal__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.special-day-modal__link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 10px;
  color: var(--blue);
  text-decoration: underline;
}

.special-day-modal__link:hover {
  color: var(--dark-blue);
}

.special-day-modal__hint {
  margin: 0.4rem 0 0 0;
  font-size: 12px;
  color: var(--grey);
}

.special-day-modal__hint--warning {
  color: var(--danger);
}

.special-day-modal__warning__text {
  margin: 0;
  padding: 0.5rem 1rem 0 1rem;
  font-size: 13px;
  color: var(--grey);
}
</style>



// WEBPACK FOOTER //
// SpecialDayModal.vue?06f7e89f